import {
  OmniElement,
  OmniStyleElement,
  OmniTooltipElement,
  OmniLoadingIndicatorElement,
  html,
  css,
  nothing,
  ref,
  createRef,
} from 'omni-ui';
import { Om2Form, om2confirm, om2alert, Om2ClampText } from 'omni-campaign-ui';
import OmniAPI from '../../services/omni-api.js';
import { downloadCSVFile } from '../../helpers/utils.js';
import { updateLocation } from '../../helpers/location.js';
import PlanningToolsAPI from '../../services/planning-tools-api.js';
import CentralizedDataAPI from '../../services/centralized-data-api.js';
import CampaignsAPI from '../../services/campaigns-api.js';
import { ChannelWheelViz } from './channel-wheel-viz.js';
import { ChannelWheelActivityCard } from './channel-wheel-activity-card.js';
import { rfcColors } from '../campaign-audience-viz/campaign-audience-rfc-legend.js';

ChannelWheelViz.register();
ChannelWheelActivityCard.register();
OmniStyleElement.register();
OmniTooltipElement.register();
Om2ClampText.register();
Om2Form.register();
OmniLoadingIndicatorElement.register();

export class ChannelWheel extends OmniElement {
  constructor() {
    super();
    this.activities = [];
    this.activityPopupRef = createRef();
    this.channelWheelVizRef = createRef();
  }

  static get properties() {
    return {
      env: { type: String },
      token: { type: String },
      context: { type: Object },
      savedData: { type: Array },
      save: { attribute: false },

      // Internal Properties
      activities: { state: true },
      legendVisible: { state: true },
      currentActivity: { state: true },
      isSharing: { state: true },
      reachChannels: { state: true },
      selectedAudience: { state: true },
      showSummary: { state: true },
      // reflect:true needed to display overlay when no market defined
      market: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .omni .box {
          margin-bottom: 2.143rem;
        }
        .omni .box .main-wrapper {
          overflow-x: auto;
          min-height: 31rem;
          display: grid;
          grid-template:
            '.      center .     ' 2rem
            'left   center right ' auto /
            17rem minmax(28rem, 73.7rem) 27.254rem;
        }
        .omni .box .main-header {
          display: flex;
          height: 2rem;
        }
        .left-pane {
          grid-area: left;
          border-right: 1px solid var(--color-border);
        }
        .center-pane {
          grid-area: center;
        }
        .right-pane {
          grid-area: right;
          border-left: 1px solid var(--color-border);
          padding: 0 0.57rem 0 1.71rem;

          /* These two lines limit the ability of height to grow. The height of the
           * right pane is determined by our channel wheel viz. Overflow activity cards
           * will scroll vertically. */
          height: 0;
          min-height: 100%;
        }
        .right-header {
          margin-right: 8.114rem;
        }
        .pane-header {
          font-weight: bold;
          font-size: var(--size-5);
          margin-right: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-border);
        }
        .vertical-scrollable {
          max-height: 100%;
          overflow-y: auto;
        }
        .hide-border-above {
          top: -1px;
          position: relative;
          background-color: white;
        }
        .legend,
        .activity-popup {
          position: absolute;
          top: 4.5rem;
          right: 3.7rem;
          box-shadow: 0 1.713rem 2.214rem -0.428rem rgba(59, 62, 63, 0.35);
          border-radius: 0.71429rem;
          background: var(--color-core-light, #fff);
        }
        .activity-popup {
          display: flex;
          flex-flow: column;
          max-height: 100%;
          width: 25.14rem;
          top: 0;
          right: 0;
          z-index: 1;
        }
        .legend {
          width: 19.14rem;
        }
        .legend .header,
        .activity-popup .header {
          padding: 0.43rem 0.5rem 0.43rem 1.14rem;
          border-bottom: 1px solid var(--color-border);
          height: 3.14rem;
        }
        .activity-popup .body {
          overflow-y: auto;
        }
        .legend-body {
          margin: 1.14rem 1.71rem;
          padding-bottom: 1.14rem;
          border-bottom: 1px solid var(--color-border);
          text-transform: capitalize;
        }
        .legend-body:last-child {
          border-bottom: 0;
        }
        .legend-gradient {
          display: flex;
        }
        .legend-gradient span {
          flex-grow: 1;
          height: 1.43rem;
        }
        .legend-body .swatch {
          display: inline-block;
          width: 1.14rem;
          height: 1.14rem;
          border-radius: 1.14rem;
          vertical-align: middle;
        }
        .audience-segment {
          height: 2.857rem;
          display: grid;
          grid-template: 'name percent' 2.857rem / 1fr auto;
          padding: 0rem 0.85714rem 0rem 1.14286rem;
          align-items: center;
          grid-gap: 2px;
          cursor: pointer;
        }
        .audience-segment:hover {
          background: var(--color-core-medium-two);
        }
        .audience-segment.selected {
          background: var(--color-blue-true);
          font-weight: bold;
          color: var(--color-primary);
        }
        .audience-segment .audience-name {
          grid-area: name;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
        .omni .root-container {
          padding-right: 0;
        }
        :host(:not([market])) div.root-container::after {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: white;
          opacity: 0.65;
          content: 'Select a market to view the list of channels';
          align-content: center;
          text-align: center;
          font-size: 1.3rem;
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.#mouseDownListener);
    this.omniAPI = new OmniAPI(this.env);
    this.planningAPI = new PlanningToolsAPI(
      this.env,
      this.context.campaignData.guid_client,
    );

    // Load saved data
    this.activities = structuredClone(this.savedData.activities ?? []);
    this.budgetAllocation = structuredClone(
      this.savedData.budgetAllocation ?? {},
    );

    // Delete orphaned activities (and budget allocations)
    // The list of associated audiences is external to this module and can change. This
    // will cause associated activities & allocation budgets to be invalid/orphaned.
    // Need to detect and remove these orphaned activities/allocations.
    this.activities
      .filter(
        activity =>
          !this.#audiences.some(
            audience =>
              activity.audienceId === this.#generateAudienceId(audience),
          ),
      )
      .forEach(orphan => this.#deleteActivity(orphan, false));
    Object.keys(this.budgetAllocation).forEach(audienceId => {
      if (!this.activities.some(x => x.audienceId === audienceId)) {
        delete this.budgetAllocation[audienceId];
      }
    });
  }

  async openSharedObjectInTab(objectId) {
    const sharedObjectsAPI = new CentralizedDataAPI(this.token, this.env);
    const response = await sharedObjectsAPI.getSharedObjects({
      uuid_object: objectId,
      client: this.context.campaignData.guid_client,
    });
    const action = response.data[0].actions.find(a => a.label.match(/^open/i));
    window.open(action.link, '_blank');
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this.#mouseDownListener);
    super.disconnectedCallback();
  }

  async updated(changedProperties) {
    if (
      changedProperties.has('context') &&
      this.#audiences.length &&
      !this.selectedAudience
    ) {
      // Select first audience
      [this.selectedAudience] = this.#audiences;
    }
    if (changedProperties.has('context') && this.context.campaignData.market) {
      this.market = this.context.campaignData.market;
      // Load reach channel data from ICP
      this.#getMarketName().then(async marketName => {
        this.reachChannels = await this.planningAPI.getReachChannels(
          marketName,
        );
      });
    }
    if (
      changedProperties.has('context') &&
      changedProperties.get('context') === undefined &&
      this.context
    ) {
      if (!this.context.campaignData.market || !this.#audiences.length) {
        om2confirm(
          "The Channel Wheel module won't display data until you choose a market and assign a Role for Comms strategy to an audience segment within the Audience Visualization Module.",
          {
            type: 'warning',
            title: 'Warning',
          },
        );
      }
    }

    if (changedProperties.has('reachChannels') && this.reachChannels) {
      // Generate "channelsById" whenever we load the reachChannels data for easy lookup
      const flattenChannels = (roots, baseObj = {}, result = {}) => {
        roots.forEach(root => {
          if (root.activities) {
            flattenChannels(
              root.activities,
              { ...baseObj, channel: root.name },
              result,
            );
          } else if (root.platforms) {
            flattenChannels(
              root.platforms,
              { ...baseObj, activity: root.name },
              result,
            );
          } else {
            // eslint-disable-next-line no-param-reassign
            result[root.id] = {
              ...baseObj,
              platform: root.name,
            };
          }
        });
        return result;
      };
      this.channelsById = flattenChannels(this.reachChannels);
    }
  }

  async #getMarketName() {
    // TODO: ICP API will accept country id from omni dictionary, but doesn't yet.
    // When it does, this logic to lookup the country name will go away and we
    // can just pass the id to ICP.
    const allMarkets = await new CampaignsAPI(
      this.token,
      this.env,
    ).getMarkets();
    const marketObj = allMarkets.find(market => market.id === this.market);
    return marketObj.name;
  }

  #dispatchModuleChange = () => {
    const { uuid_module: moduleId } = this.context;
    // Filter out orphaned/stale data (if audiences change)
    const validActivities = this.activities.filter(activity =>
      this.#audiences.some(
        audience => activity.audienceId === this.#generateAudienceId(audience),
      ),
    );
    // Filter out budget allocations if no activity has that audience
    const validAllocations = { ...this.budgetAllocation };
    Object.keys(validAllocations).forEach(audienceId => {
      if (!validActivities.some(activity => activity.audienceId === audienceId))
        delete validAllocations[audienceId];
    });

    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: () =>
          this.save({
            activities: validActivities,
            budgetAllocation: validAllocations,
          }),
      },
    });
  };

  #mouseDownListener = e => {
    if (
      this.currentActivity &&
      !e.composedPath().includes(this.activityPopupRef.value)
    ) {
      this.#resetCurrentActivity();
    }
  };

  #generateAudienceId(audience) {
    return `${audience?.uuid}-${audience?.stageId}`;
  }

  get #thoughtLeaderText() {
    let source = this.schema?.thoughtLeaderSource || '/studio/thoughtleader';
    if (!source.startsWith('/')) {
      // If source is dynamic form id, prefix with '/form/'
      source = `/form/${source}`;
    }
    const value = this.context.sharedData[source]?.value;
    return value?.text ?? value ?? '';
  }

  get #thoughtLeaderLabel() {
    return this.schema?.thoughtLeaderLabel || 'Thoughtleader';
  }

  #activityPopupTemplate(activity) {
    const isEdit = activity.type;
    const title = isEdit
      ? 'Edit channel activity'
      : 'Define channel activities';
    let schema = {
      channelIdea: {
        title: 'Channel idea',
        type: 'string',
        multiline: true,
        placeholder: 'Describe the channel idea',
      },
      assetsRequired: {
        title: 'Assets required',
        type: 'string',
        multiline: true,
        placeholder: 'Describe the assets required',
      },
      image: {
        title: 'Add image/document',
        type: 'image',
        maxSize: 1024 ** 2 * 4.5,
      },
      documentName: {
        title: 'Document name',
        type: 'string',
        placeholder: 'Enter document name',
      },
      documentURL: {
        title: 'Document URL',
        type: 'string',
        placeholder: 'Enter URL',
        pattern: 'https?://.+',
        htmlTitle: 'Document URL',
      },
    };

    let { prompt: channelIdeaPrompt } =
      this.context.assistFields?.find(f => f.field_id === 'channelIdea') ?? {};
    if (channelIdeaPrompt && this.channelsById) {
      // Replace module-specific variables here and pass assist prompt in schema
      const channel = this.channelsById[activity.id];
      const context = {
        channel: channel.channel,
        activity: channel.activity,
        audience: this.#getAudienceSegmentName(this.selectedAudience),
      };
      Object.entries(context).forEach(([key, value]) => {
        channelIdeaPrompt = channelIdeaPrompt.replaceAll(
          new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
          value,
        );
      });
      schema.channelIdea['omni-assist'] = 'autofill';
      schema.channelIdea['omni-assist-config'] = {
        prompt: channelIdeaPrompt,
        confirm: false,
      };
    }

    if (isEdit) {
      const textSchema =
        schema[activity.type === 'idea' ? 'channelIdea' : 'assetsRequired'];
      delete schema.assetsRequired;
      delete schema.channelIdea;
      schema = {
        text: textSchema,
        ...schema,
      };
    }

    const onSubmit = formData => {
      const updated = {
        userId: window.AN.sso.user.personId,
        date: new Date().toISOString(),
      };

      if (isEdit) {
        Object.assign(activity, { ...formData, updated });
      } else {
        if (formData.channelIdea) {
          this.activities.push({
            id: activity.id,
            name: activity.name,
            cpm: activity.cpm,
            type: 'idea',
            text: formData.channelIdea,
            image: formData.image,
            documentName: formData.documentName,
            documentURL: formData.documentURL,
            audienceId: this.#generateAudienceId(this.selectedAudience),
            updated,
          });
        }
        if (formData.assetsRequired) {
          this.activities.push({
            id: activity.id,
            name: activity.name,
            cpm: activity.cpm,
            type: 'asset',
            text: formData.assetsRequired,
            image: formData.image,
            documentName: formData.documentName,
            documentURL: formData.documentURL,
            audienceId: this.#generateAudienceId(this.selectedAudience),
            updated,
          });
        }
      }
      this.#resetCurrentActivity();
      this.#dispatchModuleChange();
    };

    return html`
      <div class="activity-popup" ${ref(this.activityPopupRef)}>
        <div class="header is-flex is-align-items-center">
          <span class="is-flex-grow-1">${title}</span>
          <button class="button is-text" @click=${this.#resetCurrentActivity}>
            <omni-icon
              class="is-size-1"
              icon-id="omni:interactive:close"
            ></omni-icon>
          </button>
        </div>
        <div class="body">
          <om2-form
            class="m-5"
            .data=${activity}
            .schema=${schema}
            .uiSchema=${Object.keys(schema).map(id => ({ type: 'ref', id }))}
            .submitButtonText=${isEdit ? 'Edit' : 'Add'}
            .save=${onSubmit}
            @om2-form-cancel=${this.#resetCurrentActivity}
          >
          </om2-form>
        </div>
      </div>
    `;
  }

  #createActivity(e) {
    const createName = (node, suffix = []) =>
      node.depth > 1
        ? createName(node.parent, [node.data.name, ...suffix])
        : suffix.join(' - ');
    this.currentActivity = {
      id: e.detail.node.data.id,
      name: createName(e.detail.node),
      cpm: e.detail.node.data.cpm,
    };
  }

  #editActivity(activity) {
    this.currentActivity = activity;
  }

  #deleteActivity(activity, confirm = true) {
    const doDelete = () => {
      this.activities = this.activities.filter(elem => elem !== activity);
      if (!this.activities.some(x => x.audienceId === activity.audienceId)) {
        delete this.budgetAllocation[activity.audienceId];
      }
      this.requestUpdate();
      this.#dispatchModuleChange();
    };

    if (confirm) {
      om2confirm(`Permanently delete activity (${activity.id})?`, {
        callback: doDelete,
        type: 'warning',
      });
    } else {
      doDelete();
    }
  }

  #getAudienceActivities(audience) {
    return this.activities.filter(
      activity =>
        activity.audienceId ===
        this.#generateAudienceId(audience ?? this.selectedAudience),
    );
  }

  #resetCurrentActivity() {
    if (this.currentActivity) {
      this.currentActivity = null;
      this.channelWheelVizRef.value.resetSelection();
    }
  }

  #activityCardTemplate(activity) {
    return html`
      <campaign-channel-activity-card
        image=${activity.image}
        type=${activity.type}
        name=${activity.name}
        userId=${activity.updated.userId}
        date=${activity.updated.date}
        text=${activity.text}
        documentURL=${activity.documentURL}
        documentName=${activity.documentName}
        .actions=${[
          {
            iconId: 'omni:interactive:edit',
            callback: () => this.#editActivity(activity),
            tooltip: 'Edit',
          },
          {
            iconId: 'omni:interactive:delete',
            callback: () => this.#deleteActivity(activity),
            tooltip: 'Delete',
          },
        ]}
      >
      </campaign-channel-activity-card>
    `;
  }

  get #legendTemplate() {
    const rfcTemplate = () => {
      if (!this.selectedAudience?.roleForComms) return nothing;
      return html`
        <div class="legend-body is-size-6">
          <div class="mb-3">ROLE FOR COMMS</div>
          <span
            class="swatch mr-3"
            style="background-color: ${rfcColors[
              this.selectedAudience?.roleForComms
            ]}"
          ></span>
          ${this.selectedAudience?.roleForComms}
        </div>
      `;
    };

    const cpmTemplate = () => {
      // Currently false but eventually we'll add cpm info back
      if (!this.cpm) return nothing;
      return html`
        <div class="legend-body is-size-6">
          <div class="mb-3">CPM</div>
          <div class="legend-gradient">
            ${[
              '#99D9ED',
              '#73CBE6',
              '#4DBDE0',
              '#26AFD9',
              '#00A1D2',
              '#0093CB',
            ].map(rgb => html`<span style="background-color: ${rgb};"></span>`)}
          </div>
          <div class="is-flex">
            <span class="is-flex-grow-1">Low</span>
            <span>High</span>
          </div>
        </div>
      `;
    };

    return html`
      <div class="legend">
        <div class="header is-flex is-align-items-center">
          <span class="is-flex-grow-1">Legend</span>
          <button
            class="button is-text is-size-2"
            @click=${() => {
              this.legendVisible = false;
            }}
          >
            <omni-icon icon-id="omni:interactive:minus"></omni-icon>
          </button>
        </div>
        ${cpmTemplate()} ${rfcTemplate()}
      </div>
    `;
  }

  get #channelWheelTemplate() {
    return html`
      <div class="main-wrapper">
        <div class="left-pane is-flex is-flex-direction-column">
          <div class="pane-header">Audience segments</div>
          ${this.#audiences.length
            ? html`<div class="vertical-scrollable">
                ${this.#audiences.map(
                  audience => html`
                <omni-tooltip style="width:100%">
                  <div slot="invoker" class="audience-segment ${
                    audience === this.selectedAudience ? 'selected' : ''
                  }"
                    @click=${() => {
                      this.selectedAudience = audience;
                    }}
                  >
                  <div class="audience-name">
                    ${this.#getAudienceSegmentName(audience)}
                  </div>
                  <div>(${audience.percent ?? 100}%)</div>
                  </div>
                  <div slot="content">
                    ${this.#getAudienceSegmentName(audience)}
                    (${audience.percent ?? 100}%)</div>
                </omni-tooltip>
              </div>
            `,
                )}
              </div>`
            : html`
                <div
                  class="is-flex is-align-self-center is-align-items-center has-text-centered is-flex-grow-1 has-text-shark"
                >
                  Select audience(s) <br />
                  before assigning a <br />
                  channel mix to each <br />
                  audience.
                </div>
              `}
        </div>
        <div class="center-pane">
          <campaign-channel-wheel-viz
            .thoughtLeaderText="${this.#thoughtLeaderText}"
            .thoughtLeaderLabel="${this.#thoughtLeaderLabel}"
            .roleForComms="${this.selectedAudience?.roleForComms}"
            .reachChannels=${this.market
              ? this.reachChannels
              : [{ name: '', children: [] }]}
            @channel-wheel-selection=${e => this.#createActivity(e)}
            ${ref(this.channelWheelVizRef)}
            ?disabled=${!this.#audiences.length}
          ></campaign-channel-wheel-viz>
        </div>
        <div class="right-pane is-flex is-flex-direction-column">
          <div class="pane-header">Channel activities</div>
          ${this.#getAudienceActivities()?.length
            ? html`
                <div class="vertical-scrollable hide-border-above">
                  ${this.#getAudienceActivities().map(activity =>
                    this.#activityCardTemplate(activity),
                  )}
                </div>
              `
            : html`
                <div
                  class="ml-5 mr-5 is-flex is-align-self-center is-align-items-center has-text-centered is-flex-grow-1 has-text-shark"
                >
                  Click into the channel wheel to define<br />
                  channel activities for the selected<br />
                  audience segment
                </div>
              `}
        </div>
      </div>
    `;
  }

  #getAudienceSegmentName(audience) {
    return audience.stageName
      ? `${audience.name} - ${audience.stageName}`
      : audience.name;
  }

  /**
   * Creates a data structure for the summary tab. It is used for both the summary table
   * as well as exporting the summary tab to CSV
   *
   * @async
   * @returns {Promise<Array>} The summary data structure
   */
  get #summaryData() {
    const audienceData = this.#audiences
      .filter(audience => this.#getAudienceActivities(audience).length)
      .map(audience => {
        const audienceActivities = this.#getAudienceActivities(audience)
          .reduce((activityMap, activity) => {
            const activities = activityMap.get(activity.id) ?? {
              id: activity.id,
            };
            activities[activity.type] = activity.text;
            activityMap.set(activity.id, activities);
            return activityMap;
          }, new Map())
          .values()
          .map(activities => ({
            channel: this.channelsById[activities.id],
            idea: activities.idea,
            asset: activities.asset,
          }));
        return {
          audienceId: this.#generateAudienceId(audience),
          name: this.#getAudienceSegmentName(audience),
          percent: audience.percent ?? 100,
          activities: audienceActivities,
        };
      });
    return audienceData;
  }

  get #remainingBudget() {
    let remaining = this.context.campaignData.budget;
    Object.values(this.budgetAllocation).forEach(allocation => {
      remaining -= allocation;
    });
    return remaining;
  }

  /**
   * Override "Export" module feature in step detail.
   *
   * @async
   */
  async exportModule() {
    // Return null to fall back to image export. Only override for summary tab csv
    if (!this.showSummary) return null;

    const filename = `${this.context.campaignData?.name}_Channel_Wheel_YYYYMMDD.csv`;
    const csvData = [
      [
        // initialize with csv header
        'Audience',
        'Percent',
        'P/O/E',
        'Channel',
        'Activity',
        'Ad Format',
        'Idea Description',
        'Assets Required',
      ],
    ];
    this.#summaryData.forEach(audience =>
      audience.activities.forEach(activity => {
        csvData.push([
          audience.name,
          audience.percent,
          'PAID',
          activity.channel.channel,
          activity.channel.activity,
          activity.channel.platform,
          activity.idea,
          activity.asset,
        ]);
      }),
    );
    return downloadCSVFile(csvData, filename);
  }

  #openEditModal = () => {
    updateLocation({
      setParams: { modal: `${this.context.campaignData.so_type}-edit` },
    });
  };

  get #summaryLastRowTemplate() {
    const { budget, currency } = this.context.campaignData;
    const remainingBudget = this.#remainingBudget;
    let label;
    let labelClass;
    let labelDescription;
    if (!budget) {
      label = 'Total budget:';
      labelClass = 'has-text-danger';
      labelDescription = 'You need to set a campaign budget';
    } else if (remainingBudget === 0) {
      label = 'Remaining budget:';
      labelClass = 'has-text-success';
      labelDescription = 'Your budget has been allocated';
    } else {
      label = 'Remaining budget:';
      labelClass = 'has-text-danger';
      labelDescription =
        'You must allocate your full budget across all audience segments';
    }

    return html`
      <div class="columns mt-5">
        <div class="column is-8"></div>
        <div class="column">
          <div class="${labelClass} has-text-weight-bold">${label}</div>
          <div class="has-text-shark is-size-7">${labelDescription}</div>
        </div>
        <div class="column">
          ${budget
            ? remainingBudget.toLocaleString('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })
            : html`<button
                class="button is-outlined is-size-5"
                @click=${this.#openEditModal}
              >
                Set campaign budget
              </button>`}
        </div>
      </div>
    `;
  }

  get #summaryTemplate() {
    if (!this.reachChannels)
      return html`
        <omni-loading-indicator class="mt-6 mb-6"></omni-loading-indicator>
      `;

    return html`
      <div class="ml-5 pt-4">
        ${this.#summaryData.map(
          (audience, audienceIndex) => html`
            ${audienceIndex === 0 ? nothing : html` <hr class="mt-5" /> `}

            <div class="columns">
              <div class="column">
                <div class="has-text-weight-bold mt-5">
                  ${audience.name} (${audience.percent ?? 100}%)
                </div>
              </div>
              <div class="column is-2">
                <div class="has-text-shark is-size-7">Budget Allocation</div>
                <input
                  class="input"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="0"
                  ?disabled=${!this.context.campaignData.budget}
                  .value=${this.budgetAllocation[audience.audienceId] || null}
                  @change=${({ target }) => {
                    if (!target.value.match(/^[0-9]+$/)) {
                      target.value = ''; // eslint-disable-line no-param-reassign
                    }
                  }}
                  @input=${e => {
                    const parsed = parseInt(e.target.value, 10);
                    if (parsed) {
                      this.budgetAllocation[audience.audienceId] = parsed;
                    } else {
                      delete this.budgetAllocation[audience.audienceId];
                    }
                    this.#dispatchModuleChange();
                  }}
                />
              </div>
            </div>

            ${audience.activities.map(activities => {
              const { channel } = activities;

              return html`
                <div class="columns mt-5">
                  <div class="column">
                    <div class="has-text-shark is-size-7">P/O/E</div>
                    <div>PAID</div>
                  </div>
                  <div class="column">
                    <div class="has-text-shark is-size-7">Channel</div>
                    <div>${channel.channel}</div>
                  </div>
                  <div class="column">
                    <div class="has-text-shark is-size-7">Activity</div>
                    <div>${channel.activity}</div>
                  </div>
                  <div class="column">
                    <div class="has-text-shark is-size-7">Ad Format</div>
                    <div>${channel.platform}</div>
                  </div>
                  <div class="column">
                    <div class="has-text-shark is-size-7">Idea Description</div>
                    <om2-clamp-text
                      style="word-break: break-word;"
                      .text="${activities.idea ?? '-'}"
                    ></om2-clamp-text>
                  </div>
                  <div class="column">
                    <div class="has-text-shark is-size-7">Assets Required</div>
                    <om2-clamp-text
                      style="word-break: break-word;"
                      .text="${activities.asset ?? '-'}"
                    ></om2-clamp-text>
                  </div>
                </div>
              `;
            })}
          `,
        )}
        <hr class="mt-5" />
        ${this.#summaryLastRowTemplate}
      </div>
    `;
  }

  #shareToICP = async () => {
    if (this.isSharing) return;
    if (!this.context.campaignData.budget) {
      om2confirm(
        'To create an Investment and Channel Plan, first add your campaign budget before allocating your full budget for each of your audience segments.',
        {
          title: 'You need to add a campaign budget',
          type: 'warning',
          callback: [
            {
              func: this.#openEditModal,
              label: 'Add Budget',
            },
          ],
        },
      );
      return;
    }

    if (this.#remainingBudget !== 0) {
      om2confirm(
        'Allocate your campaign budget across all your audience segments within the summary tab',
        {
          title: 'You need to allocate your campaign budget',
          type: 'warning',
          callback: [
            {
              func: () => {
                this.showSummary = true;
              },
              label: 'Allocate Budget',
            },
          ],
        },
      );
      return;
    }

    this.isSharing = true;
    try {
      const allocationAudiences = {};
      this.activities.forEach(activity => {
        if (!allocationAudiences[activity.audienceId]) {
          const audience = this.#audiences.find(
            a => this.#generateAudienceId(a) === activity.audienceId,
          );
          if (!audience) return; // skip orphaned activities
          // eslint-disable-next-line no-param-reassign
          allocationAudiences[activity.audienceId] = {
            ...audience,
            channels: [],
            budget: this.budgetAllocation[activity.audienceId],
          };
        }
        if (
          allocationAudiences[activity.audienceId].channels.every(
            channel => channel.id !== activity.id,
          )
        ) {
          allocationAudiences[activity.audienceId].channels.push(activity);
        }
      });
      const response = await this.planningAPI.createAllocationPlan(
        this.context.campaignData,
        Object.values(allocationAudiences),
        await this.#getMarketName(),
      );
      om2confirm('Would you like to open it in a new tab?', {
        callback: [
          {
            label: 'Open',
            func: () => this.openSharedObjectInTab(response.omni_object_uuid),
          },
        ],
        title: 'An Allocation Plan was successfully created',
        type: 'success',
        // To close the modal they have to either click Cancel or Open
        closeOnModalClick: false,
      });
    } catch (e) {
      const campaignStart = new Date(this.context.campaignData?.start_date);
      const campaignEnd = new Date(this.context.campaignData?.end_date);
      if (
        !(
          this.context.campaignData?.start_date &&
          this.context.campaignData?.end_date
        ) ||
        campaignEnd - campaignStart < 1000 * 3600 * 24 * 5
      ) {
        om2alert(
          'Campaign duration must be 5 days or greater to create an Investment and Channel Plan.',
        );
        return;
      }

      try {
        om2alert((await e.json()).detail);
      } catch {
        om2alert('Creating Allocation Plan failed');
      }
    } finally {
      this.isSharing = false;
    }
  };

  get #audiences() {
    return this.context.sharedData?.['/studio/audience_segments']?.value ?? [];
  }

  render() {
    return html`
      <omni-style>
        <div class="box root-container is-relative">
          <div class="main-header">
            <div class="is-flex is-align-items-center is-flex-grow-1">
              <button
                class="button ${this.showSummary ? 'is-text' : 'is-primary'}
                mr-3"
                @click=${() => {
                  this.showSummary = false;
                }}
              >
                Channel wheel
              </button>
              <button
                class="button ${this.showSummary ? 'is-primary' : 'is-text'}"
                ?disabled=${this.activities.length === 0}
                @click=${() => {
                  this.showSummary = true;
                }}
              >
                Summary
              </button>
            </div>

            <div class="is-flex is-align-items-center right-header">
              <omni-switch
                class=${this.selectedAudience?.roleForComms ? '' : 'is-hidden'}
                .checked=${this.legendVisible}
                @change=${e => {
                  this.legendVisible = e.detail.checked;
                }}
              >
                <span>View legend</span>
              </omni-switch>

              <omni-tooltip show-on-disabled>
                <button
                  class="button is-text ml-4 ${this.isSharing
                    ? 'is-loading is-light'
                    : ''}"
                  slot="invoker"
                  @click=${this.#shareToICP}
                  ?disabled=${this.isSharing || !this.planningAPI.canCreate()}
                >
                  <omni-icon
                    class="${this.isSharing ? 'is-hidden' : ''}"
                    icon-id="omni:object:plan"
                  ></omni-icon>
                </button>
                <div slot="content">
                  Distribute to Investment and Channel Planner
                </div>
              </omni-tooltip>
            </div>
          </div>

          ${this.showSummary
            ? this.#summaryTemplate
            : this.#channelWheelTemplate}
          ${this.legendVisible ? this.#legendTemplate : nothing}
          ${this.currentActivity
            ? this.#activityPopupTemplate(this.currentActivity)
            : nothing}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-channel-wheel', ChannelWheel);
